import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { API } from "@/context/AuthContext";
import { inr, inrShort } from "@/pages/Dashboard";

const DRILLS = [
  { value: "special", label: "Stock type → Category → Material" },
  { value: "supplier", label: "Stock type → Subcontractor → Material" },
  { value: "project", label: "Project → WBS → Category → Material" },
  { value: "category", label: "Category → Material → Receipt" },
  { value: "plant", label: "Plant → Sloc → Material → Receipt" },
  { value: "wbs", label: "WBS → Material → Receipt" },
  { value: "source", label: "Receipt type → Material" },
];
const fmtQ = (v) => (v == null ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 3 }));

function flatten(nodes, expanded, depth = 0, prefix = "") {
  const rows = [];
  (nodes || []).forEach((node, i) => {
    const id = prefix ? `${prefix}.${i}` : `${i}`;
    rows.push({ kind: "node", id, depth, node });
    if (expanded.has(id)) {
      if (node.children?.length) rows.push(...flatten(node.children, expanded, depth + 1, id));
      else if (node.level === "material" && node.detail) rows.push({ kind: "detail", id: `${id}~d`, depth: depth + 1, node });
    }
  });
  return rows;
}

function LotsDetail({ node, colSpan, bucket }) {
  const d = node.detail;
  return (
    <TableRow className="bg-slate-50/70">
      <TableCell colSpan={colSpan} className="p-0">
        <div className="px-10 py-4 fade-in-up space-y-2" data-testid={`stock-tree-detail-${node.key}`}>
          <p className="text-xs text-slate-600"><span className="font-semibold text-slate-800">{node.key}</span> · {d.description || "—"} · {d.plant}{d.storage_location ? ` / ${d.storage_location}` : ""}{d.special_stock ? ` · ${d.special_stock}` : ""} · {d.status}{d.supplier ? <span className="ml-2 rounded bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-amber-900" data-testid="stock-tree-detail-supplier">at {d.supplier_name || d.supplier} ({d.supplier})</span> : null}{d.wbs ? <span className="ml-2 font-mono text-slate-500">WBS {d.wbs}</span> : null}
            <span className="ml-2 text-slate-400">closing {fmtQ(d.closing_qty)} {d.unit} × ₹{Number(d.unit_price || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })} = {inr(d.closing_value)}</span></p>
          <table className="w-full text-xs">
            <thead><tr className="text-slate-400 uppercase tracking-wider text-left">
              <th className="py-1 font-medium">Receipt date</th><th className="py-1 font-medium">Mat. doc</th><th className="py-1 font-medium">PO / Order</th><th className="py-1 font-medium">Type</th><th className="py-1 font-medium">Via transfer</th><th className="py-1 font-medium">WBS</th>
              <th className="py-1 font-medium text-right">Qty aged</th><th className="py-1 font-medium text-right">Value</th><th className="py-1 font-medium text-right">Age</th><th className="py-1 font-medium">Bracket</th>
            </tr></thead>
            <tbody>
              {d.lots.filter((l) => !bucket || l.band === bucket).map((l, i) => (
                <tr key={i} className={`border-t border-slate-100 ${l.date ? "text-slate-600" : "text-amber-700"}`}>
                  <td className="py-1">{l.date || "—"}</td><td className="py-1 font-mono">{l.doc || "—"}</td><td className="py-1 font-mono">{l.ref || "—"}</td>
                  <td className="py-1">{l.source}</td><td className="py-1 font-mono text-sky-700" title="Inherited from the sending plant's inward (plant→plant transfer doc)">{l.via || "—"}</td><td className="py-1 font-mono">{l.wbs || "—"}</td>
                  <td className="py-1 text-right">{fmtQ(l.qty)}{l.receipt_qty != null && l.receipt_qty > l.qty + 1e-6 ? <span className="text-slate-400"> / {fmtQ(l.receipt_qty)}</span> : ""}</td>
                  <td className="py-1 text-right font-medium text-slate-800">{inr(l.value)}</td><td className="py-1 text-right">{l.age != null ? `${l.age} d` : "—"}</td><td className="py-1">{l.band}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </TableCell>
    </TableRow>
  );
}

export function StockAgeingTree({ params, bucket, runKey }) {
  const [drill, setDrill] = useState("category");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/reports/stock-ageing/tree`, { params: { ...params, drill } }); setData(r.data); setExpanded(new Set()); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, [params, drill]);
  useEffect(() => { load(); }, [load, runKey]);
  const toggle = (id) => setExpanded((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bands = data?.bands || [];
  const rows = data ? flatten(data.tree, expanded) : [];
  return (
    <Card className="border-slate-200 shadow-sm" data-testid="stock-ageing-tree-card">
      <CardHeader className="pb-3 flex flex-row items-center justify-between flex-wrap gap-3">
        <div>
          <CardTitle className="text-lg font-semibold text-slate-800">Drill-down {bucket && <span className="ml-2 rounded-full bg-slate-900 text-white text-xs font-medium px-2.5 py-1">{bucket}</span>}</CardTitle>
          <p className="text-xs text-slate-400 mt-0.5">{bucket ? `Items with stock in the ${bucket} bracket · expand a material to see the receipt documents behind it` : "Expand a material to see the receipt documents (material doc / PO / order) that make up its aged stock"}{loading ? " · loading…" : ""}</p>
        </div>
        <Tabs value={drill} onValueChange={setDrill}><TabsList className="flex-wrap h-auto">{DRILLS.map((o) => <TabsTrigger key={o.value} value={o.value} data-testid={`stock-drill-${o.value}`}>{o.label}</TabsTrigger>)}</TabsList></Tabs>
      </CardHeader>
      <CardContent className="overflow-x-auto p-0">
        <Table data-testid="stock-ageing-tree-table">
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Hierarchy</TableHead>
            {bands.map((b) => <TableHead key={b} className={`text-[10px] uppercase tracking-wider text-right whitespace-nowrap ${bucket === b ? "text-slate-900 font-bold bg-amber-50" : "text-slate-500"}`}>{b}</TableHead>)}
            <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 text-right">Total</TableHead>
            <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 text-right">Qty</TableHead>
            <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 text-right">#</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => r.kind === "detail" ? <LotsDetail key={r.id} node={r.node} colSpan={bands.length + 4} bucket={bucket} /> : (
              <TableRow key={r.id} className="border-b border-slate-100 hover:bg-slate-50/60 cursor-pointer" onClick={() => toggle(r.id)} data-testid={`stock-tree-node-${r.node.level}-${r.node.key}`}>
                <TableCell className="text-sm">
                  <div className="flex items-center gap-2" style={{ paddingLeft: r.depth * 18 }}>
                    <ChevronRight className={`h-3.5 w-3.5 text-slate-400 transition-transform duration-200 ${expanded.has(r.id) ? "rotate-90" : ""}`} />
                    <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 border border-slate-200 rounded px-1 py-0.5 shrink-0">{r.node.level_label}</span>
                    <span className={r.node.level === "material" ? "font-mono text-slate-700" : "font-medium text-slate-800"}>{r.node.key}</span>
                    {r.node.level === "material" && <span className="text-xs text-slate-400 truncate max-w-[280px]">· {r.node.detail.description || ""}{r.node.detail.storage_location ? ` · ${r.node.detail.plant}/${r.node.detail.storage_location}` : ` · ${r.node.detail.plant}`}{r.node.detail.supplier && drill !== "supplier" ? ` · at ${r.node.detail.supplier_name || r.node.detail.supplier}` : ""}</span>}
                  </div>
                </TableCell>
                {bands.map((b) => <TableCell key={b} className={`text-right text-sm ${bucket === b ? "bg-amber-50/60 font-semibold text-slate-800" : "text-slate-600"}`}>{r.node.buckets[b] ? inrShort(r.node.buckets[b]) : "—"}</TableCell>)}
                <TableCell className="text-right text-sm font-bold text-slate-900">{inrShort(r.node.total)}</TableCell>
                <TableCell className="text-right text-xs text-slate-500">{fmtQ(r.node.qty)}</TableCell>
                <TableCell className="text-right text-xs text-slate-500">{r.node.count}</TableCell>
              </TableRow>
            ))}
            {data && data.tree.length === 0 && <TableRow><TableCell colSpan={bands.length + 4} className="text-center text-sm text-slate-400 py-8">No stock for the selected filters</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
